import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fullstorySelectors } from '@fullstory/redux';
import { Modal } from '@fullstory/components';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
import { useHistory, useLocation } from 'react-router-dom';
import { generateRoutePath, HOME_ROUTE } from '@fullstory/routes';

const { getOrgId, getOrgName } = fullstorySelectors;
const ORG_KEY = 'active_org';
const QUERY_PARAM = 'previousOrg';

export const CrossOrgPrompt: React.FC = () => {
  const orgId = useSelector(getOrgId);
  const orgName = useSelector(getOrgName);
  const history = useHistory();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setShowModal(!!queryParams.get(QUERY_PARAM));
  }, [queryParams]);

  useEffect(() => {
    if (orgId) {
      window.localStorage.setItem(ORG_KEY, orgId);
    }
  }, [orgId]);

  useEffect(() => {
    const storageCallback = (event: StorageEvent) => {
      if (event.key === ORG_KEY && event.newValue) {
        queryParams.set(QUERY_PARAM, event.oldValue ?? '');
        window.onunload = null;
        window.location.assign(
          `${generateRoutePath(HOME_ROUTE, {
            orgId: event.newValue,
          })}?${queryParams.toString()}`,
        );
      }
    };
    window.addEventListener('storage', storageCallback);

    return () => window.removeEventListener('storage', storageCallback);
  }, [history, queryParams]);

  return (
    <Modal
      data-component="CrossOrgPrompt"
      size="small"
      onCloseSignal={() => setShowModal(false)}
      open={showModal}
      disableBackdropClick
    >
      <Modal.Header onCloseSignal={() => setShowModal(false)}>
        <FormattedMessage
          defaultMessage="The organization you're logged in to changed"
          id="ZxAmEx"
        />
      </Modal.Header>
      <Modal.Body>
        <FormattedMessage
          defaultMessage="You can only be logged in to one organization at a time. You have been redirected to your home page of the active organization, <b>{orgName}</b>."
          values={{ orgName, b: val => <b>{val}</b> }}
          id="Xuipwh"
        />
      </Modal.Body>
      <Modal.FooterActions
        secondaryAction={{
          action: () => {
            window.location.assign('/orgChooser');
          },
          text: intl.formatMessage({ defaultMessage: 'Change organization', id: 'RBzmqG' }),
        }}
        primaryAction={{
          action: () => {
            queryParams.delete(QUERY_PARAM);
            history.replace({
              pathname: location.pathname,
              search: queryParams.toString(),
              state: location.state,
            });
            setShowModal(false);
          },
          text: intl.formatMessage({ defaultMessage: 'Got it!', id: 'yTrowV' }),
        }}
      />
    </Modal>
  );
};
